import { createSelector } from "@reduxjs/toolkit";

import { assert, nodeDesignsByIdSelector, type MultilingualNodeConfig } from "@skilitics/nautilus-core-designer-data";

import { LayoutNodeSpecs } from "./layoutConfig.ts";

type Design<T extends string = string> = { typeId: T; config: MultilingualNodeConfig };

const isLayoutNode = (v: Design): v is Design<keyof LayoutNodeSpecs> => v.typeId in LayoutNodeSpecs;

type Entry<T extends Design = Design, Id extends number | string = number | string> = [Id, T];

type LayoutEntry = Entry<Design<keyof LayoutNodeSpecs>, number>;

const isLayoutEntry = (e: Entry): e is [string, Design<keyof LayoutNodeSpecs>] => isLayoutNode(e[1]);

const layoutDesignEntriesSelector = createSelector([nodeDesignsByIdSelector], (designsById) =>
  Object.entries(designsById)
    .filter(isLayoutEntry)
    .map(([idStr, { typeId, config }]): LayoutEntry => [Number(idStr), { typeId, config }]),
);

const layoutIdMapsSelector = createSelector([layoutDesignEntriesSelector], (layoutDesignEntries) => {
  const contentIdByLayoutIdMap = new Map<number, number>();
  const layoutIdByContentIdMap = new Map<number, number>();
  const layoutTitleByIdMap = new Map<number, string>();

  for (const [layoutId, { typeId, config }] of layoutDesignEntries) {
    const spec = LayoutNodeSpecs[typeId];
    const contentId = config.nodeReferences?.[spec.nodeReferenceProperty];
    const title = config[spec.titleProperty] ?? "";
    // A named slot (slotRef) is filled by its host at runtime; it owns no content item so it
    // doesn't participate in the contentId <-> layoutId maps.
    const isNamedSlot = "slotRef" in config && !!config.slotRef;
    assert(
      contentId || spec.optional || isNamedSlot,
      `Layout node ${typeId}:${layoutId} expected to have config property ${spec.nodeReferenceProperty} and does not.`,
    );
    if (!contentId) continue;
    assert(typeof contentId === "number");
    assert(typeof title === "string");

    layoutIdByContentIdMap.set(contentId, layoutId);
    contentIdByLayoutIdMap.set(layoutId, contentId);
    layoutTitleByIdMap.set(layoutId, title ?? "");
  }

  return {
    contentIdByLayoutIdMap,
    layoutIdByContentIdMap,
    layoutTitleByIdMap,
  };
});

export const contentIdByLayoutIdMapSelector = createSelector(
  [layoutIdMapsSelector],
  ({ contentIdByLayoutIdMap }) => contentIdByLayoutIdMap,
);

export const layoutIdByContentIdMapSelector = createSelector(
  [layoutIdMapsSelector],
  ({ layoutIdByContentIdMap }) => layoutIdByContentIdMap,
);

export const layoutTitleByIdMapSelector = createSelector(
  [layoutIdMapsSelector],
  ({ layoutTitleByIdMap }) => layoutTitleByIdMap,
);
